import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useGraphqlPagedQuery, {
  PagedQueryOptions,
} from '@elementary/common-ui/hooks/useGraphqlPagedQuery';

// Generic helper for rendering a grouped table where each group is backed by
// its own independently-paginated GraphQL query (the "one paged query per
// group" pattern, e.g. grouped Test Results / Incidents).
//
// Why a hook that returns elements instead of a plain loop of hooks: the set
// of groups is dynamic (it comes from data), and the Rules of Hooks forbid
// calling a hook a variable number of times. The established workaround is to
// render one renderless component per group, each hosting a single paged
// query. This hook hides that: it builds those fetcher elements for you
// (`groupFetchers`) and exposes the lifted-up rows/pagination via accessors.
//
// IMPORTANT: the returned `groupFetchers` MUST be rendered in the DOM for the
// hook to do anything. The per-group queries live inside those elements, so if
// you don't render `groupFetchers`, no data is ever fetched and every accessor
// (`getRows`, `isInitialLoading`, etc.) stays empty. They render nothing
// visible, so place them anywhere in your tree, e.g.:
//   const { groupFetchers, getRows } = useGroupedPagedQuery(...);
//   return (<>{groupFetchers}<MyTable ... /></>);

export type GroupPagedState<TRow> = {
  rows: TRow[];
  hasNextPage: boolean;
  isFetching: boolean;
};

type FetcherProps<TItem, TResponse, TRow> = {
  groupKey: string;
  options: PagedQueryOptions<TItem, TResponse>;
  mapItems: (items: TItem[]) => TRow[];
  onState: (groupKey: string, state: GroupPagedState<TRow>) => void;
  registerFetchNext: (groupKey: string, fetchNextPage: () => void) => void;
  unregister: (groupKey: string) => void;
};

const GroupFetcher = <TItem, TResponse, TRow>({
  groupKey,
  options,
  mapItems,
  onState,
  registerFetchNext,
  unregister,
}: FetcherProps<TItem, TResponse, TRow>) => {
  const { data, isFetching, isLoading, fetchNextPage, hasNextPage } =
    useGraphqlPagedQuery<TItem, TResponse>(options);

  const mapItemsRef = useRef(mapItems);
  mapItemsRef.current = mapItems;

  const rows = useMemo<TRow[]>(() => mapItemsRef.current(data), [data]);

  useEffect(() => {
    onState(groupKey, {
      rows,
      hasNextPage,
      isFetching: isFetching || isLoading,
    });
  }, [groupKey, rows, hasNextPage, isFetching, isLoading, onState]);

  useEffect(() => {
    registerFetchNext(groupKey, fetchNextPage);
  }, [groupKey, fetchNextPage, registerFetchNext]);

  useEffect(() => () => unregister(groupKey), [groupKey, unregister]);

  return null;
};

export type UseGroupedPagedQueryArgs<TItem, TResponse, TRow> = {
  groupKeys: string[];
  buildQueryOptions: (groupKey: string) => PagedQueryOptions<TItem, TResponse>;
  mapItems: (items: TItem[]) => TRow[];
};

export type UseGroupedPagedQueryResult<TRow> = {
  /**
   * Renderless elements hosting the per-group queries. MUST be rendered in the
   * DOM (anywhere in the tree) — otherwise no group is ever fetched.
   */
  groupFetchers: ReactNode;
  getRows: (groupKey: string) => TRow[];
  getGroupState: (groupKey: string) => GroupPagedState<TRow> | undefined;
  fetchNextGroupPage: (groupKey: string | undefined) => void;
  isAnyFetching: boolean;
  isAnyFetchingWithRows: boolean;
  isInitialLoading: boolean;
};

const useGroupedPagedQuery = <TItem, TResponse, TRow>({
  groupKeys,
  buildQueryOptions,
  mapItems,
}: UseGroupedPagedQueryArgs<
  TItem,
  TResponse,
  TRow
>): UseGroupedPagedQueryResult<TRow> => {
  const [stateByKey, setStateByKey] = useState<
    Record<string, GroupPagedState<TRow>>
  >({});

  const fetchNextRef = useRef<Map<string, () => void>>(new Map());

  const handleState = useCallback(
    (groupKey: string, state: GroupPagedState<TRow>) => {
      setStateByKey((prev) => ({ ...prev, [groupKey]: state }));
    },
    [],
  );

  const registerFetchNext = useCallback(
    (groupKey: string, fetchNextPage: () => void) => {
      fetchNextRef.current.set(groupKey, fetchNextPage);
    },
    [],
  );

  const unregister = useCallback((groupKey: string) => {
    fetchNextRef.current.delete(groupKey);
    setStateByKey((prev) => {
      if (!(groupKey in prev)) return prev;
      const next = { ...prev };
      delete next[groupKey];
      return next;
    });
  }, []);

  const groupFetchers = useMemo(
    () =>
      groupKeys.map((groupKey) => (
        <GroupFetcher<TItem, TResponse, TRow>
          key={groupKey}
          groupKey={groupKey}
          options={buildQueryOptions(groupKey)}
          mapItems={mapItems}
          onState={handleState}
          registerFetchNext={registerFetchNext}
          unregister={unregister}
        />
      )),
    [
      groupKeys,
      buildQueryOptions,
      mapItems,
      handleState,
      registerFetchNext,
      unregister,
    ],
  );

  const getGroupState = useCallback(
    (groupKey: string) => stateByKey[groupKey],
    [stateByKey],
  );

  const getRows = useCallback(
    (groupKey: string) => stateByKey[groupKey]?.rows ?? [],
    [stateByKey],
  );

  const fetchNextGroupPage = useCallback(
    (groupKey: string | undefined) => {
      if (groupKey === undefined) return;
      const state = stateByKey[groupKey];
      if (!state?.hasNextPage || state.isFetching) return;
      fetchNextRef.current.get(groupKey)?.();
    },
    [stateByKey],
  );

  const isAnyFetching = useMemo(
    () => Object.values(stateByKey).some((state) => state.isFetching),
    [stateByKey],
  );

  const isAnyFetchingWithRows = useMemo(
    () =>
      Object.values(stateByKey).some(
        (state) => state.isFetching && state.rows.length > 0,
      ),
    [stateByKey],
  );

  const isInitialLoading =
    groupKeys.length > 0 &&
    groupKeys.some((groupKey) => stateByKey[groupKey] === undefined);

  return {
    groupFetchers,
    getRows,
    getGroupState,
    fetchNextGroupPage,
    isAnyFetching,
    isAnyFetchingWithRows,
    isInitialLoading,
  };
};

export default useGroupedPagedQuery;
